/**
 * FilterOptionsContext
 *
 * Fires parallel aggregate calls for each enabled filter field
 * after the page mounts. Each call groups by a single field, so the response
 * is just a list of distinct values with counts; far smaller than a
 * multi-dimension cross-product call.
 *
 * Results are memoized for the lifetime of ReportingView so the filter
 * popup opens instantly once the data has arrived.
 */

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetAggregateAssets } from "@hooks/use-get/aggregate-assets";
import type { AggregateAssetResult } from "@hooks/use-get/aggregate-assets";
import { getDistinctAggregateValues } from "@hooks/use-get/aggregate-assets/utils";
import type { ReportFilterOptions } from "../types";

const DEFAULT_OPTIONS: ReportFilterOptions = {};

const FilterOptionsContext =
  createContext<ReportFilterOptions>(DEFAULT_OPTIONS);

/** Extract sorted unique non-empty string values for a single field from aggregate results. */
function uniqueValues(data: AggregateAssetResult[], field: string): string[] {
  return getDistinctAggregateValues(data, field);
}

interface FilterOptionsProviderProps extends PropsWithChildren {
  enabledFields?: readonly string[];
  /** Maps each filter field name to the postType of its data source endpoint. Defaults to 'asset'. */
  fieldPostTypeMap?: Record<string, 'asset' | 'ticket' | 'user'>;
}

export const FilterOptionsProvider = ({ children, enabledFields: enabledFieldsProp, fieldPostTypeMap }: FilterOptionsProviderProps) => {
  const { getAggregateAssets } = useGetAggregateAssets();

  const enabledFields = enabledFieldsProp ?? [];

  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(() => {
    return enabledFields.reduce((acc, columnName) => {
      acc[columnName] = [];
      return acc;
    }, {} as Record<string, string[]>);
  });

  const hasFetched = useRef(false);
  const prevFieldsKey = useRef("");

  const fieldsKey = enabledFields.join(",");

  useEffect(() => {
    if (fieldsKey !== prevFieldsKey.current) {
      hasFetched.current = false;
      prevFieldsKey.current = fieldsKey;
    }
  }, [fieldsKey]);

  useEffect(() => {
    if (hasFetched.current) return;
    if (enabledFields.length === 0) return;
    hasFetched.current = true;

    const fetchPromises = enabledFields.map((columnName) =>
      getAggregateAssets({
        groupBy: [columnName],
        postType: fieldPostTypeMap?.[columnName] ?? 'asset',
      }).then((result) => ({
        columnName,
        values: uniqueValues(result.data || [], columnName),
      })).catch(() => ({
        columnName,
        values: [],
      }))
    );

    Promise.all(fetchPromises)
      .then((results) => {
        const newFilterValues: Record<string, string[]> = {};
        results.forEach(({ columnName, values }) => {
          newFilterValues[columnName] = values;
        });
        setFilterValues(newFilterValues);
      })
      .catch(() => {
        // Leave defaults in place; dropdowns render without pre-populated choices.
      });
    // fieldsKey is the stable proxy for enabledFields/fieldPostTypeMap by content, avoiding refetches on new-but-equal prop references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAggregateAssets, fieldsKey]);

  const options = useMemo((): ReportFilterOptions => filterValues, [filterValues]);

  return (
    <FilterOptionsContext.Provider value={options}>
      {children}
    </FilterOptionsContext.Provider>
  );
};

/** Read the cached filter options. Must be used inside FilterOptionsProvider. */
export const useFilterOptionsContext = () =>
  useContext(FilterOptionsContext);
